import React, { useEffect, useState } from "react";
import axios from "axios";
import { MailIcon, PhoneIcon, UserIcon, Loader2, LockIcon, KeyIcon, ArrowLeft } from "lucide-react";
import { API_BASE_URL, LOGO_URL } from "../../../config";
import { RegisterModalProps } from "../../../types/auth";

const REGISTER_URL = `${API_BASE_URL}/user/public/register`;
const COMPLETE_REG_URL = `${API_BASE_URL}/user/public/complete-registration`;

export default function RegisterModal({ 
  isOpen, 
  onClose,
  onSwitchToLogin
}: RegisterModalProps) {
  const initialFormState = {
    firstName: "",
    lastName: "",
    mobileNo: "",
    emailId: "",
  };

  const [step, setStep] = useState(1); // 1: Info, 2: OTP, 3: Password
  const [formData, setFormData] = useState(initialFormState);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [onSuccess, setOnSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage("");
  };

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    try {
      await axios.post(REGISTER_URL, {
        fullName: `${formData.firstName} ${formData.lastName}`,
        mobileNumber: formData.mobileNo,
        emailId: formData.emailId,
        roleId: 2,
      });
      setStep(2);
      setSuccessMessage("OTP sent to your email!");
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP.");
      return;
    }
    setStep(3);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      await axios.post(COMPLETE_REG_URL, {
        emailId: formData.emailId,
        otp,
        password,
      });
      setOnSuccess(true);
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || "Failed to complete registration.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setStep(1);
    setErrorMessage("");
    setSuccessMessage("");
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setOnSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm">
      {!onSuccess ? (
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-300 space-y-6">
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
          >
            ✕
          </button>

          <div className="text-center">
            <img
                src={LOGO_URL}
                alt="Company Logo"
                className="mx-auto h-12 w-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-900">
                {step === 1 && "Create Your Account"}
                {step === 2 && "Verify Email"}
                {step === 3 && "Set Password"}
            </h2>
            <p className="mt-2 text-sm text-gray-500">
                {step === 1 && "Start your journey with us"}
                {step === 2 && `Enter the OTP sent to ${formData.emailId}`}
                {step === 3 && "Secure your account with a password"}
            </p>
          </div>

          {errorMessage && (
            <div className="p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200 flex items-start space-x-3">
                 <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div>
                    <h3 className="font-bold text-sm mb-1">Error</h3>
                    <p className="text-xs leading-relaxed">{errorMessage}</p>
                </div>
            </div>
          )}
          {successMessage && (
            <div className="p-4 text-sm text-green-700 bg-green-50 rounded-lg border border-green-200 flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-[#2563eb]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div>
                    <h3 className="font-bold text-sm mb-1">Success</h3>
                    <p className="text-xs leading-relaxed">{successMessage}</p>
                </div>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleInfoSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <div className="relative group">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                      <UserIcon className="size-5" />
                    </span>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="John"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] outline-none transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <div className="relative group">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                      <UserIcon className="size-5" />
                    </span>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Doe"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] outline-none transition"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                    <PhoneIcon className="size-5" />
                  </span>
                  <input
                    type="tel"
                    name="mobileNo"
                    placeholder="+91 9876543210"
                    required
                    value={formData.mobileNo}
                    onChange={handleChange}
                    className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                    <MailIcon className="size-5" />
                  </span>
                  <input
                    type="email"
                    name="emailId"
                    placeholder="john@example.com"
                    required
                    value={formData.emailId}
                    onChange={handleChange}
                    className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] outline-none transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg font-semibold text-white brand-bg brand-hover-bg flex items-center justify-center gap-2 transition-all shadow-sm active:scale-[0.98] disabled:opacity-60"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue to Verification"}
              </button>

              <div className="text-center mt-6">
                  <p className="text-sm text-gray-500">
                      Already have an account?{" "}
                      <button
                          type="button"
                          onClick={onSwitchToLogin}
                          className="font-semibold text-gray-900 hover:text-[#2563eb] transition-colors"
                      >
                          Login
                      </button>
                  </p>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-center font-bold">Enter 6-Digit OTP</label>
                <input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-[#f8f9fa] border-b-2 border-gray-200 py-3 text-center text-3xl font-bold tracking-[0.5em] focus:border-[#2563eb] outline-none transition"
                />
              </div>

              <div className="flex gap-4">
                <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                >
                    <ArrowLeft className="size-4" /> Back
                </button>
                <button
                    type="submit"
                    className="flex-[2] py-2.5 rounded-lg font-semibold text-white brand-bg brand-hover-bg transition shadow-sm active:scale-[0.98]"
                >
                    Verify & Continue
                </button>
              </div>
              
              <p className="text-sm text-center text-gray-500">
                Didn't receive code? <button type="button" onClick={handleInfoSubmit} className="text-[#2563eb] font-semibold hover:underline">Resend OTP</button>
              </p>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleCompleteRegistration} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                    <LockIcon className="size-5" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] outline-none transition"
                  />
                   <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {showPassword ? <KeyIcon className="size-5" /> : <LockIcon className="size-5" />}
                    </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative group">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#2563eb] transition-colors">
                    <LockIcon className="size-5" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:ring-2 focus:ring-[#2563eb] outline-none transition"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                >
                    <ArrowLeft className="size-4" /> Back
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-[2] py-2.5 rounded-lg font-semibold text-white brand-bg brand-hover-bg flex items-center justify-center gap-2 transition shadow-sm active:scale-[0.98] disabled:opacity-60"
                >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Set Password & Finish"}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-10 text-center animate-in fade-in zoom-in duration-500 space-y-6">
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center bg-green-50 shadow-inner">
               <svg className="w-12 h-12 text-[#2563eb]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Aboard! 🎉</h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              Your account has been activated successfully. You can now login using your email and password.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              onClose();
              onSwitchToLogin();
            }}
            className="w-full py-3 rounded-lg font-bold text-white brand-bg brand-hover-bg transition shadow-md hover:shadow-lg active:scale-95"
          >
            Login Now
          </button>
        </div>
      )}
    </div>
  );
}
